import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Modal,
  Stack,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useUpdateProductStatus } from "./useActionButton";
import { PRODUCT_STATUS } from "../../../../models/Product";
import { rootPaths } from "../../../../routes/routePaths";
import { mono } from "../../../../theme/colors";
import Text from "../../../atoms/Text";
import ExclamationIcon from "../../../icons/ui/ExclamationIcon";
import TrashcanIcon from "../../../icons/ui-actions/TrashcanIcon";

type Props = {
  productId: string;
  productName?: string;
};

const ButtonActionDelete = ({ productId, productName }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const { updateProductStatus, isLoading } = useUpdateProductStatus();
  const navigate = useNavigate();

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleDelete = async () => {
    await updateProductStatus(productId, PRODUCT_STATUS.DELETED);
    handleCloseModal();
    navigate(rootPaths.rewardInventoryPortal);
  };

  return (
    <Box data-testid="action-delete-component">
      <IconButton
        onClick={handleOpenModal}
        data-testid="action-delete-modal-button"
        aria-label="icon-del-button"
      >
        <TrashcanIcon fill={mono.grey[2]} />
      </IconButton>

      <Modal
        open={isModalOpen}
        onClose={handleCloseModal}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        data-testid="modal-component"
      >
        <Box
          width={532}
          bgcolor="mono.white"
          borderRadius={2.5}
          pb={3}
          overflow="hidden"
        >
          <Box px={2.5} py={2} bgcolor="mono.light.grey">
            <Text variant={"title_medium"}>Delete Item</Text>
          </Box>

          <Stack p={3} alignItems="center">
            <ExclamationIcon width={48} height={48} />

            <Stack mt={2} mb={4} alignItems="center">
              <Text variant={"body_large"}>
                Are you sure you want to delete
              </Text>
              <Text variant={"title_small"}>{productId}</Text>
              {productName && (
                <Text variant={"title_small"}>{productName}</Text>
              )}
            </Stack>

            <Stack direction="row" spacing={1} width={256}>
              <Button
                variant="secondary"
                size="small"
                fullWidth={true}
                onClick={handleCloseModal}
                data-testid="close-modal-button"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="small"
                fullWidth={true}
                disabled={isLoading}
                startIcon={isLoading && <CircularProgress size={20} />}
                onClick={handleDelete}
                data-testid="delete-button"
              >
                Delete
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>
    </Box>
  );
};

export default ButtonActionDelete;
